import { DatePipe } from '@angular/common';
import {
    AfterContentChecked,
    AfterViewInit,
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    Validators
} from '@angular/forms';
import { I18n } from '@ngx-translate/i18n-polyfill';
import {
    endOfDay,
    getISODay,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays
} from 'date-fns';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ClassNames } from '../../../constants/class-names.constants';
import { Color } from '../../../constants/color.constants';
import { DateFormat } from '../../../constants/date-format.constants';
import { IconPaths, Icons } from '../../../constants/icons.constants';
import { Orientation } from '../../../constants/orientation.constants';
import { ActionSheetService } from '../../../services/action-sheet/action-sheet.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { BaseFormControlComponent } from '../../forms/base-form-control.component';
import { DatePickerType } from '../date-picker/constants/date-picker-type';
import { IDateRange } from '../date-picker/models/date-range';

@LifecycleAware()
@Component({
    selector: 'lsq-date-select',
    templateUrl: './lsq-date-select.component.html',
    styleUrls: ['./lsq-date-select.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqDateSelectComponent),
            multi: true
        }
    ]
})
export class LsqDateSelectComponent extends BaseFormControlComponent<Date | IDateRange>
    implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
    @Input() public id = '';
    @Input() public basicLabel = false;
    @Input() public label = 'Date:';
    @Input() public type?: DatePickerType = DatePickerType.Single;
    @Input() public allowFuture = false;
    @Input() public lowerLimit: Date;
    @Input() public upperLimit: Date;
    @Input() public sideBySide = false;
    @Input() public defaultRangeOption?:
        | string
        | 'today'
        | 'weekToDate'
        | 'monthToDate'
        | 'quarterToDate'
        | 'yearToDate'
        | 'lastYear';
    @Input() public arrow = true;
    @Input() public parentClass = '';
    @Input() public inputClass = '';
    @Input() public border?: 'dark' | 'light' = Color.Dark;
    @Input() public calendarLabel?: string;
    @Input() public hideOptions = false;
    @Input() public displayRangePresetNameAsValue? = false;
    @Input() public placeholder = '';

    @Output() public rangeOptionSelected = new EventEmitter<string>();

    public newValue: Date | IDateRange = { start: null, end: null };
    public isExpanded = false;
    public viewportClass = '';
    public typeClass = '';
    public rangeOptions = [
        { id: 'today', value: 'today', style: { 'flex-grow': 0.5 } },
        { id: 'week-to-date', value: 'weekToDate' },
        { id: 'month-to-date', value: 'monthToDate' },
        { id: 'quarter-to-date', value: 'quarterToDate' },
        { id: 'year-to-date', value: 'yearToDate' },
        { id: 'last-year', value: 'lastYear', style: { 'flex-grow': 0.7 } },
        { id: 'custom-range', value: 'custom' }
    ];
    public rangeOptionsDisplay = {
        today: 'Today',
        weekToDate: 'Week-to-date',
        monthToDate: 'Month-to-date',
        quarterToDate: 'Quarter-to-date',
        yearToDate: 'Year-to-date',
        lastYear: 'Last Year',
        custom: 'Custom Range'
    };
    public selectedRange = this.rangeOptions.find((where) => where.value === 'custom').value;
    public isSmallDevice = false;
    public isSmallDevice$: Observable<boolean>;
    public icons = Icons;
    public iconPaths: typeof IconPaths;
    public color = Color;
    public inputIcon: Icons;
    public orientation = Orientation;
    public classNames = ClassNames;
    public datePickerType = DatePickerType;
    private _datePlaceholder = this._i18n('Select Date');
    private _dateRangePlaceholder = this._i18n('Select Date Range');
    public disableApplyButton = true;
    public startDateValue: Date = null;
    public endDateValue: Date = null;
    public singleDateValue: Date = null;
    public startDateRangeValidator = (control: AbstractControl): { [key: string]: any } | null => {
        if (control.value && this.endDateValue && control.value > this.endDateValue) {
            return {
                invalidDate: {
                    message: this.i18n('Start date cannot exceed End.')
                }
            };
        }
        return null;
    };
    public endDateRangeValidator = (control: AbstractControl): { [key: string]: any } | null => {
        if (control.value && this.startDateValue && control.value < this.startDateValue) {
            return {
                invalidDate: {
                    message: this.i18n('End date cannot precede Start.')
                }
            };
        }
        return null;
    };
    public futureDateValidator = (control: AbstractControl): { [key: string]: any } | null => {
        const todayDate = endOfDay(new Date());
        if (control.value && control.value > todayDate && !this.allowFuture) {
            return {
                invalidDate: {
                    message: this.i18n('Future date cannot be selected.')
                }
            };
        }

        return null;
    };
    public dateRangeFormGroup: FormGroup = new FormGroup({
        startDateInput: new FormControl(this.startDateValue, [
            Validators.required,
            this.startDateRangeValidator,
            this.futureDateValidator
        ]),
        endDateInput: new FormControl(this.endDateValue, [
            Validators.required,
            this.endDateRangeValidator,
            this.futureDateValidator
        ])
    });
    public singleDateFormGroup: FormGroup = new FormGroup({
        singleDateInput: new FormControl('', [Validators.required, this.futureDateValidator])
    });

    constructor(
        public injector: Injector,
        public responsiveService: ResponsiveService,
        public actionSheetService: ActionSheetService,
        public datePipe: DatePipe,
        private _i18n: I18n
    ) {
        super(injector);
        this.newValue = this.isRangePicker() ? { start: null, end: null } : null;
        this.iconPaths = IconPaths;
        this.isSmallDevice$ = this.responsiveService.smBelow.pipe(forLifeOf(this));
        this.isSmallDevice$.subscribe((isSmallDevice) => {
            this.isSmallDevice = isSmallDevice;
        });
    }

    // Lifecycle methods.
    public ngOnInit(): void {
        this.inputIcon = this.basicLabel ? Icons.Calendar : Icons.ChevronDown;
        // Sync `this.newValue` with `this.model` if `this.model` changes.
        this.modelChangeSubject.pipe(forLifeOf(this)).subscribe((model: Date | IDateRange) => {
            this.setNewValue(model);
            this.resetDatePickerState();
        });

        this.responsiveService.smBelow
            .pipe(
                forLifeOf(this),
                filter((isSmBelow) => isSmBelow)
            )
            .subscribe(() => {
                this.isExpanded = true;
            });

        // Set initial value.
        Utilities.executeAfterRender(() => {
            this.defaultRangeOption = this.defaultRangeOption || (this.model as IDateRange)?.range;
            if (this.defaultRangeOption) {
                this.selectRangeOption(
                    this.rangeOptions.find((where) => where.value === this.defaultRangeOption)
                        .value,
                    false
                );
                this.apply();
            } else {
                this.setNewValue(this.model);
            }
        });

        if (!this.placeholder) {
            if (this.type === DatePickerType.Range || this.type === DatePickerType.MonthRange) {
                this.placeholder = this._dateRangePlaceholder;
            } else {
                this.placeholder = this._datePlaceholder;
            }
        }
        const startDateControl = this.dateRangeFormGroup.controls.startDateInput;
        const endDateControl = this.dateRangeFormGroup.controls.endDateInput;
        startDateControl.statusChanges.subscribe(() => {
            Utilities.executeAfterRender(() => {
                if (startDateControl.valid && endDateControl.invalid) {
                    this.updateInputValues();
                    endDateControl.updateValueAndValidity();
                }
            });
        });
        endDateControl.statusChanges.subscribe(() => {
            Utilities.executeAfterRender(() => {
                if (endDateControl.valid && startDateControl.invalid) {
                    this.updateInputValues();
                    startDateControl.updateValueAndValidity();
                }
            });
        });
    }

    public ngAfterViewInit(): void {
        this.fixPrematurelyDirtiedInputs();
    }

    public ngAfterContentChecked(): void {
        if (this.isRangePicker()) {
            var rangeValue = this.getAsDateRange(this.newValue);
            this.disableApplyButton =
                !rangeValue?.start || !rangeValue?.end || !this.dateRangeFormGroup.valid;
        } else {
            this.disableApplyButton =
                !this.getAsDate(this.newValue) || !this.singleDateFormGroup.valid;
        }
    }

    // Lifecycle methods.
    public ngOnChanges(): void {
        // Set viewport class.
        if (this.inputClass.indexOf('bg-') === -1) {
            this.viewportClass += ` ${ClassNames.BackgroundWhite}`;
        }

        // Set type class.
        this.typeClass = 'lsq-date-select-input-' + this.type;
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    private resetDatePickerState(): void {
        if (!this.model) {
            this.newValue = this.isRangePicker() ? { start: null, end: null } : null;
            this.startDateValue = null;
            this.endDateValue = null;
            this.singleDateValue = null;
        } else {
            this.newValue = this.model;
            this.updateInputValues();
        }
        //reset inputs
        this.dateRangeFormGroup.controls.startDateInput.reset(this.startDateValue);
        this.dateRangeFormGroup.controls.endDateInput.reset(this.endDateValue);
        this.singleDateFormGroup.controls.singleDateInput.reset(this.singleDateValue);
    }

    public toggleExpanded(isExpanded?: boolean): void {
        if (typeof isExpanded !== 'undefined') {
            this.isExpanded = isExpanded;
        } else {
            this.isExpanded = !this.isExpanded;
        }

        if (!this.isExpanded) {
            this.resetDatePickerState();
            this.blur();
        }
    }

    private blur(): void {
        const input = this.elementRef.nativeElement.querySelector('.lsq-date-select-input');
        if (input) {
            input.blur();
        }
    }

    public hasDarkBackground(): boolean {
        return this.parentClass.indexOf(ClassNames.BackgroundDark) > -1;
    }

    public setNewValue(value: Date | IDateRange): void {
        if (value) {
            const oldValue = this.newValue;
            if (!!(value as IDateRange).start) {
                if (
                    oldValue &&
                    (oldValue as IDateRange).start === (value as IDateRange).start &&
                    (oldValue as IDateRange).end === (value as IDateRange).end
                ) {
                    return;
                } else {
                    this.newValue = value;
                }
            } else {
                if (oldValue && oldValue === value) {
                    return;
                } else {
                    this.newValue = value;
                }
            }
        }
    }

    public handleInputBlur(): void {
        this.updateInputValues();
        this.blur();
    }

    public handleSingleDateInputChange(value: Date): void {
        if (!value) {
            this.newValue = null;
            return;
        }
        this.setNewValue(value);
        this.selectCustomRange(false);
    }

    public handleStartDateInputChange(value: Date): void {
        if (!value) {
            this.selectCustomRange(true);
            return;
        }
        this.setNewValue({ start: value, end: this.endDateValue });
        this.selectCustomRange(false);
    }

    public handleEndDateInputChange(value: Date): void {
        if (!value) {
            this.selectCustomRange(true);
            return;
        }
        this.setNewValue({ start: this.startDateValue, end: value });
        this.selectCustomRange(false);
    }

    public apply(): void {
        this.handleChange(this.newValue);
        this.rangeOptionSelected.emit(this.selectedRange);
        if (!this.isSmallDevice) {
            this.toggleExpanded(false);
        }
        if (this.isSmallDevice) {
            this.actionSheetService.closeActionSheet();
            this.resetDatePickerState();
        }
    }

    public handleDayClick(): void {
        if (this.sideBySide) {
            this.selectCustomRange();
        }
        this.updateInputValues();
    }

    public isRangePicker(): boolean {
        return this.type === DatePickerType.Range || this.type === DatePickerType.MonthRange;
    }

    public get expandedClass(): string {
        return this.isExpanded ? ClassNames.IsExpanded : '';
    }

    private selectToday(): void {
        const todayStart = startOfDay(new Date());
        const todayEnd = endOfDay(new Date());
        this.selectedRange = 'today';
        this.newValue = { start: todayStart, end: todayEnd, range: this.selectedRange };
    }

    private selectWeekToDate(): void {
        const today = endOfDay(new Date());
        this.selectedRange = 'weekToDate';
        this.newValue = { start: startOfWeek(today), end: today, range: this.selectedRange };
    }

    private selectMonthToDate(): void {
        const today = endOfDay(new Date());
        this.selectedRange = 'monthToDate';
        this.newValue = { start: startOfMonth(today), end: today, range: this.selectedRange };
    }

    private selectQuarterToDate(): void {
        const today = endOfDay(new Date());
        const currentMonth = today.getMonth();
        const startingMonth = Math.floor(currentMonth / 3) * 3;
        this.selectedRange = 'quarterToDate';
        this.newValue = {
            start: new Date(today.getFullYear(), startingMonth, 1),
            end: today,
            range: this.selectedRange
        };
    }

    private selectYearToDate(): void {
        const today = endOfDay(new Date());
        this.selectedRange = 'yearToDate';
        this.newValue = { start: startOfYear(today), end: today, range: this.selectedRange };
    }

    private selectLastYear(): void {
        const yearStart = startOfYear(new Date());
        const endOfLastYear = endOfDay(subDays(yearStart, 1));
        const startOfLastYear = startOfYear(endOfLastYear);
        this.selectedRange = 'lastYear';
        this.newValue = { start: startOfLastYear, end: endOfLastYear, range: this.selectedRange };
    }

    private selectCustomRange(clearCalendar = false, clearInputs = false): void {
        this.selectedRange = 'custom';
        if (clearCalendar) {
            this.newValue = {
                start: null,
                end: null
            };
        }
        if (clearInputs) {
            this.startDateValue = null;
            this.endDateValue = null;
            this.dateRangeFormGroup.reset();
        }
        this.newValue['range'] = this.selectedRange;
    }

    public selectRangeOption(which: string, clearSelection = true): void {
        switch (which) {
            case 'today':
                this.selectToday();
                break;
            case 'weekToDate':
                this.selectWeekToDate();
                break;
            case 'monthToDate':
                this.selectMonthToDate();
                break;
            case 'quarterToDate':
                this.selectQuarterToDate();
                break;
            case 'yearToDate':
                this.selectYearToDate();
                break;
            case 'lastYear':
                this.selectLastYear();
                break;
            case 'custom':
            default:
                this.selectCustomRange(clearSelection, clearSelection);
                break;
        }

        this.updateInputValues();
    }

    public updateInputValues(): void {
        if (this.isRangePicker()) {
            this.startDateValue = this.getAsDateRange(this.newValue)?.start ?? this.startDateValue;
            this.endDateValue = this.getAsDateRange(this.newValue)?.end ?? this.endDateValue;
        } else {
            this.singleDateValue = this.getAsDate(this.newValue) ?? this.singleDateValue;
        }
    }

    public getAsDate(value: Date | IDateRange): Date {
        if (value instanceof Date) {
            return value;
        }
        return null;
    }

    public getAsDateRange(value: Date | IDateRange): IDateRange {
        if (
            !value ||
            value instanceof Date ||
            typeof value !== 'object' ||
            (!value.start && !value.end)
        ) {
            return { start: null, end: null, range: this.selectedRange };
        }
        return value;
    }

    public safelyDisplayDate(date: Date): string {
        const transform = (value: Date) => this.datePipe.transform(value, DateFormat.LsqShortYear);
        if (date instanceof Date) {
            return `${transform(date) || ''}`;
        }
        return '';
    }
}
